// Pomodoro settings editor. Mirrors the pomodoro fields in the web Settings
// M.E.T.I.S category (src/app/settings/page.tsx / settings-context.tsx).
// Music folder / selected music are stubs on web too — skipped here.
import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { ThemedSwitch } from './ThemedSwitch';
import { makeThemedStyles } from '../../v10/theme';
import { SectionTitle, Field, GlassCard } from '../../v10/ui';
import { useSettingColumn } from './useSettingColumn';

const BELLS = ['bell-1', 'bell-2', 'bell-3'];

function nextBell(current: string): string {
  const idx = BELLS.indexOf(current);
  return BELLS[(idx + 1) % BELLS.length];
}

export default function PomodoroEditor({ userId }: { userId: string | undefined }) {
  const styles = useStyles();
  const { value: enabled, setValue: setEnabled } = useSettingColumn<boolean>(userId, 'pomodoro_enabled', false);
  const { value: workTime, setValue: setWorkTime } = useSettingColumn<number>(userId, 'pomodoro_work_time', 20);
  const { value: breakTime, setValue: setBreakTime } = useSettingColumn<number>(userId, 'pomodoro_break_time', 5);
  const { value: breakStartBell, setValue: setBreakStartBell } = useSettingColumn<string>(
    userId,
    'pomodoro_break_start_bell',
    'bell-1'
  );
  const { value: breakEndBell, setValue: setBreakEndBell } = useSettingColumn<string>(
    userId,
    'pomodoro_break_end_bell',
    'bell-2'
  );
  const { value: repeat, setValue: setRepeat } = useSettingColumn<boolean>(userId, 'pomodoro_repeat', false);

  const handleWorkTimeChange = (text: string) => {
    const parsed = parseInt(text, 10);
    setWorkTime(Number.isNaN(parsed) ? 20 : parsed);
  };

  const handleBreakTimeChange = (text: string) => {
    const parsed = parseInt(text, 10);
    setBreakTime(Number.isNaN(parsed) ? 5 : parsed);
  };

  return (
    <GlassCard style={styles.card}>
      <SectionTitle style={styles.sectionGap}>Pomodoro</SectionTitle>

      <View style={styles.row}>
        <Text style={styles.label}>Enabled</Text>
        <ThemedSwitch value={enabled} onValueChange={setEnabled} />
      </View>

      <View style={styles.row}>
        <Text style={styles.label}>Work time (min)</Text>
        <Field
          inputStyle={styles.numberInput}
          keyboardType="number-pad"
          value={String(workTime)}
          onChangeText={handleWorkTimeChange}
        />
      </View>

      <View style={styles.row}>
        <Text style={styles.label}>Break time (min)</Text>
        <Field
          inputStyle={styles.numberInput}
          keyboardType="number-pad"
          value={String(breakTime)}
          onChangeText={handleBreakTimeChange}
        />
      </View>

      <View style={styles.row}>
        <Text style={styles.label}>Break start bell</Text>
        <Text style={styles.pickerValue} onPress={() => setBreakStartBell(nextBell(breakStartBell))}>
          {breakStartBell}
        </Text>
      </View>

      <View style={styles.row}>
        <Text style={styles.label}>Break end bell</Text>
        <Text style={styles.pickerValue} onPress={() => setBreakEndBell(nextBell(breakEndBell))}>
          {breakEndBell}
        </Text>
      </View>

      <View style={styles.row}>
        <Text style={styles.label}>Repeat</Text>
        <ThemedSwitch value={repeat} onValueChange={setRepeat} />
      </View>
    </GlassCard>
  );
}

const useStyles = makeThemedStyles(({ t, radii }) =>
  StyleSheet.create({
  sectionGap: { marginTop: 18 },
  card: {
    padding: 14,
    marginTop: 8,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
  },
  label: { fontSize: 14, fontWeight: '700', color: t.ivory },
  numberInput: {
    minWidth: 60,
    textAlign: 'center',
  },
  pickerValue: {
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    paddingHorizontal: 14,
    paddingVertical: 8,
    fontSize: 13,
    fontWeight: '700',
    color: t.goldBright,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: t.lineFaint,
    boxShadow: `0 4px 14px ${t.glowSoft}`,
  },
}),
);
